import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '@app/components/base/base.component';
import { MenusServices } from '@app/services/menus.service';
import { NotificationService } from '@app/services/notifications.service';
import { AuthService } from '@app/common/auth.service';
import { ModalService } from '@app/components/modal/modal.service';
import { ClientsService } from '@app/pages/clients/services/clients.service';
import { DOCUMENT } from '@angular/common';
import { ClientApi, PersonType } from '@app/pages/clients/interfaces/client';
import { StandardQuoteService } from '@app/pages/quote/standard-quote/standard-quote-service.service';
import { FormStep } from '@app/components/client-form-modal-wrapper/client-form-modal-wrapper.component';
import { naturalPersonNameValidator, smartNameValidator } from '@app/core/util/name-validator';
import { environment } from '@env/environment.local';
import { isStandardQuoteCustomerOnlyLayout } from '@app/pages/quote/open-quote/model/open-quote.model';

@Component({
  template: ""
})
export class ClientWrapperComponent
  extends BaseComponent
  implements OnInit, ControlValueAccessor, Validator, OnChanges
{
  formCustomer: FormGroup;
  checked: boolean = false;
  btnDisabled: boolean = true;
  @Input() creationUser: any;
  /** Forzar solo lectura (p. ej. quote ligada a solicitud de crédito). */
  @Input() forceReadOnly: boolean = false;
  @Input() hasDeals: boolean = false;
  customerIdSaved = input<number | null>(null);
  @Output() requestOpenModal: EventEmitter<void> = new EventEmitter<void>();
  @Output() clearDeals: EventEmitter<void> = new EventEmitter<void>();
  readonly selectedCustomerId = output<number>();

  @ViewChild('modalNewClient') modalNewClient!: TemplateRef<any>;
  @ViewChild('modalSelectClient') modalSelectClient!: TemplateRef<any>;
  @ViewChild('personTypeChangeConfirmation') personTypeChangeConfirmation!: TemplateRef<any>;


  public readonly clientsService = inject(ClientsService);
  public readonly modalService = inject(ModalService);
  private readonly document = inject(DOCUMENT);
  private readonly fb = inject(FormBuilder);
  public readonly authService = inject(AuthService);
  public readonly quoteService = inject(StandardQuoteService);

  readonly selectedClient = signal<any>(null);
  readonly tempSelectedClient = signal<any>(null);
  readonly isClientWithRFC = signal<boolean>(true);
  readonly featureLoyaltyFeriaQuotation = environment.featureLoyaltyFeriaQuotation;

  customerCopy: any = null;
  readonly isClientWithRFCCopy = signal<boolean>(true);
  customerIdCopy: number | null = null;
  /** Cliente sin RFC cargado desde cotización: no validar formato de nombre hasta que el usuario edite. */
  private skipNameFormatValidation = false;
  private pendingPersonTypeChange: ClientApi | null = null;
  private pendingPersonTypeWithoutRfc: PersonType | null = null;
  private previousPersonType: PersonType | null = null;

  // Properties for template compatibility
  userId: number = 0;
  isModeEdition: boolean = false;

  // Helper function for template
  Number = Number;

  /** Solo lectura: valor del check «Cliente de feria» en el formulario. */
  customerFeriaReadonlyYesNo(): string {
    if (!this.featureLoyaltyFeriaQuotation) {
      return '';
    }
    const raw = this.formCustomer?.getRawValue?.() as
      | { feriaCustomer?: boolean }
      | undefined;
    return Boolean(raw?.feriaCustomer) ? 'Sí' : 'No';
  }

  constructor(
    route: ActivatedRoute,
    menu: MenusServices,
    notification: NotificationService,
  ) {
    super(route, menu, notification);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['forceReadOnly'] && this.forceReadOnly) {
      try {
        Object.values(this.formCustomer?.controls ?? {}).forEach((c: any) => c?.disable?.());
      } catch {
        // noop
      }
    }
    if (changes['creationUser']) {
      if (this.creationUser) {
        if (this.creationUser != this.authService.currentUserIds) {
          this.formCustomer.controls['personType'].disable();
          this.formCustomer.controls['firstName'].disable();
          this.formCustomer.controls['secondName'].disable();
          this.formCustomer.controls['lastName'].disable();
          this.formCustomer.controls['mothersLastName'].disable();
          this.formCustomer.controls['feriaCustomer']?.disable();
        }
        this.applyCustomerModeFromQuote(this.customerIdSaved());
      }
    }
  }

  ngOnInit() {
    this.createForm();
  }

  createForm() {
    this.formCustomer = this.fb.group({
      id: [null, []],
      creatioDate: [null, []],
      personType: ['', []],
      firstName: [, [smartNameValidator()]],
      secondName: [, [smartNameValidator()]],
      lastName: [, [smartNameValidator()]],
      mothersLastName: [, [smartNameValidator()]],
      rfc: [, []],
      customerName: [, []],
      isFromProLeaseNet: [false, []],
      feriaCustomer: [false, []],
    });

    this.formCustomer.get('personType')?.valueChanges.subscribe((personType: PersonType) => {
      this.handlePersonTypeValueChange(personType);
    });

    this.formCustomer.valueChanges.subscribe(() => {
      this.onChange(this.getRawValue());
      this.onTouched();
      this.btnDisabled = !this.formCustomer.valid;
      this.onValidatorChange();
    });
  }

  private handlePersonTypeValueChange(personType: PersonType): void {
    const previousPersonType = this.previousPersonType;
    if (
      !this.isClientWithRFC() &&
      previousPersonType &&
      personType &&
      previousPersonType !== personType
    ) {
      if (this.isClosedQuote()) {
        this.formCustomer.controls['personType'].setValue(previousPersonType, { emitEvent: false });
        this.validatorChange();
        this.showNotificationError(
          'El tipo de persona debe corresponder al tipo original de la cotización.'
        );
        return;
      }

      if (this.hasDeals) {
        this.pendingPersonTypeWithoutRfc = personType;
        this.formCustomer.controls['personType'].setValue(previousPersonType, { emitEvent: false });
        this.validatorChange();
        this.openPersonTypeChangeConfirmation();
        return;
      }
    }

    this.previousPersonType = personType || null;
    this.validatorChange();
  }

  protected enableStrictNameValidation(): void {
    if (!this.skipNameFormatValidation) {
      return;
    }
    this.skipNameFormatValidation = false;
    this.validatorChange();
  }

  validatorChange() {
    const personType = this.formCustomer.controls['personType'].value;
    const isNaturalPerson = personType === 'NATURAL_PERSON';
    const isLegalPerson = personType === 'LEGAL_PERSON';

    // Configurar validadores según tipo de persona
    if (isLegalPerson) {
      if (this.skipNameFormatValidation && !this.isClientWithRFC()) {
        this.formCustomer.controls['firstName'].setValidators([Validators.required]);
        this.formCustomer.controls['lastName'].clearValidators();
        this.formCustomer.controls['mothersLastName'].clearValidators();
        this.formCustomer.controls['secondName'].clearValidators();
        this.formCustomer.controls['lastName'].setErrors(null);
        this.formCustomer.controls['mothersLastName'].setErrors(null);
        this.formCustomer.controls['secondName'].setErrors(null);
      } else {
      // Para persona moral/legal: solo validar firstName como razón social
      // Permite espacios y algunos caracteres especiales, pero no permite solo símbolos
      this.formCustomer.controls['firstName'].setValidators([Validators.required, smartNameValidator()]);

      // Limpiar campos que no aplican para persona moral
      this.formCustomer.controls['lastName'].clearValidators();
      this.formCustomer.controls['mothersLastName'].clearValidators();
      this.formCustomer.controls['secondName'].clearValidators();

      this.formCustomer.controls['secondName'].setValue(null);
      this.formCustomer.controls['lastName'].setValue(null);
      this.formCustomer.controls['mothersLastName'].setValue(null);

      this.formCustomer.controls['secondName'].setErrors(null);
      this.formCustomer.controls['lastName'].setErrors(null);
      this.formCustomer.controls['mothersLastName'].setErrors(null);
      }
    } else if (isNaturalPerson) {
      if (this.skipNameFormatValidation && !this.isClientWithRFC()) {
        this.formCustomer.controls['firstName'].setValidators([Validators.required]);
        this.formCustomer.controls['lastName'].setValidators([Validators.required]);
        this.formCustomer.controls['secondName'].clearValidators();
        this.formCustomer.controls['mothersLastName'].clearValidators();
        this.formCustomer.controls['secondName'].setErrors(null);
        this.formCustomer.controls['mothersLastName'].setErrors(null);
      } else {
        this.formCustomer.controls['firstName'].setValidators([Validators.required, naturalPersonNameValidator]);
        this.formCustomer.controls['lastName'].setValidators([Validators.required, naturalPersonNameValidator]);
        this.formCustomer.controls['secondName'].setValidators([naturalPersonNameValidator]);
        this.formCustomer.controls['mothersLastName'].setValidators([naturalPersonNameValidator]);
      }
    }

    // Configurar validación de RFC según el modo
    if (this.isClientWithRFC()) {
      const linkedCustomerId = this.customerIdSaved();
      if (linkedCustomerId == null) {
        this.formCustomer.controls['rfc'].setValidators([Validators.required]);
      } else {
        this.formCustomer.controls['rfc'].clearValidators();
        this.formCustomer.controls['rfc'].setErrors(null);
      }
      this.formCustomer.controls['firstName'].clearValidators();
      this.formCustomer.controls['lastName'].clearValidators();
      this.formCustomer.controls['secondName'].clearValidators();
      this.formCustomer.controls['mothersLastName'].clearValidators();
      this.formCustomer.controls['firstName'].setErrors(null);
      this.formCustomer.controls['lastName'].setErrors(null);
      this.formCustomer.controls['secondName'].setErrors(null);
      this.formCustomer.controls['mothersLastName'].setErrors(null);
    } else {
      this.formCustomer.controls['rfc'].clearValidators();
      this.formCustomer.controls['rfc'].setErrors(null);
    }

    // Caso especial: clientes de ProLeaseNet pueden no tener apellido
    if (this.formCustomer.controls["isFromProLeaseNet"].value) {
      this.formCustomer.controls['lastName'].setValidators(null);
      this.formCustomer.controls['lastName'].setErrors(null);
    }

    // Actualizar validación de todos los campos
    this.formCustomer.controls['rfc'].updateValueAndValidity();
    this.formCustomer.controls['firstName'].updateValueAndValidity();
    this.formCustomer.controls['secondName'].updateValueAndValidity();
    this.formCustomer.controls['lastName'].updateValueAndValidity();
    this.formCustomer.controls['mothersLastName'].updateValueAndValidity();
    this.formCustomer.updateValueAndValidity();
    this.onValidatorChange();
  }

  private normalizeCustomerPatch(value: Partial<ClientApi> | null): Partial<ClientApi> | null {
    if (!value) {
      return value;
    }
    if (value.personType === PersonType.LEGAL_PERSON) {
      return {
        ...value,
        firstName: value.customerName || value.firstName || '',
        customerName: value.customerName || value.firstName || undefined,
      };
    }
    if (value.personType === PersonType.NATURAL_PERSON) {
      const resolved = this.clientsService.resolveNaturalPersonNameFields(value);
      return {
        ...value,
        ...resolved,
        customerName: resolved.customerName ?? undefined,
      };
    }
    return value;
  }

  public updateCustomerForm(client: ClientApi | null) {
    if (this.document) {
      this.document.body.style.overflow = 'auto';
    }
    if(client !== null){
      const normalized = this.normalizeCustomerPatch(client) as ClientApi;
      const dto = {
        personType: normalized.personType,
        customerName: null,
        firstName:
          normalized.personType === PersonType.NATURAL_PERSON
            ? normalized.firstName
            : normalized.customerName || normalized.firstName,
        lastName: normalized.lastName,
        mothersLastName: normalized.mothersLastName,
        secondName: normalized.secondName,
        rfc: normalized.rfc,
        isFromProLeaseNet: normalized?.isFromProLeaseNet || false,
        feriaCustomer: Boolean((normalized as any)?.feriaCustomer),
      }
      this.formCustomer.patchValue(dto);
      this.selectedClient.set(client);
      this.previousPersonType = normalized.personType ?? null;
      if(client.id !== null) {
        this.selectedCustomerId.emit(Number(client.id));
      }
      this.validatorChange();
      this.formCustomer.controls['lastName'].setValidators(null);
      this.formCustomer.controls['lastName'].setErrors(null);
      this.formCustomer.get('lastName')?.updateValueAndValidity();
      this.reload();
    }
  }

  // Alias method for template compatibility
  public updateClientForm(client: ClientApi | null) {
    this.updateCustomerForm(client);
  }


  saveAndUpdateClientForm(event: { client: ClientApi | null; isNew: boolean; isUpdated: boolean }) {
    if (event.client && this.hasPersonTypeChanged(event.client)) {
      if (this.isClosedQuote()) {
        this.showNotificationError(
          'El RFC debe corresponder al tipo de persona original de la cotización.'
        );
        this.restoreCustomerMode();
        return;
      }

      if (this.hasDeals) {
        this.pendingPersonTypeChange = event.client;
        setTimeout(() => this.openPersonTypeChangeConfirmation());
        return;
      }
    }

    this.updateCustomerForm(event.client);
    this.clearCustomerModeBackup();
    if(event.isNew){
      this.clientsService.showSaveClientSuccess();
    }
  }

  handlePersonTypeChangeConfirmation(confirmed: boolean): void {
    const pending = this.pendingPersonTypeChange;
    const pendingPersonTypeWithoutRfc = this.pendingPersonTypeWithoutRfc;
    this.pendingPersonTypeChange = null;
    this.pendingPersonTypeWithoutRfc = null;
    this.modalService.closeModal();

    if (pendingPersonTypeWithoutRfc !== null) {
      if (confirmed) {
        this.previousPersonType = pendingPersonTypeWithoutRfc;
        this.formCustomer.controls['personType'].setValue(pendingPersonTypeWithoutRfc);
        this.clearDeals.emit();
      }
      return;
    }

    if (!confirmed || !pending) {
      this.restoreCustomerMode();
      return;
    }

    this.updateCustomerForm(pending);
    this.clearCustomerModeBackup();
    this.clearDeals.emit();
  }

  private openPersonTypeChangeConfirmation(): void {
    this.modalService.component = this.personTypeChangeConfirmation;
    this.modalService.openModal();
  }

  private hasPersonTypeChanged(client: ClientApi): boolean {
    const currentPersonType =
      this.selectedClient()?.personType ??
      this.customerCopy?.personType ??
      this.formCustomer.get('personType')?.value;
    return Boolean(currentPersonType && client.personType && currentPersonType !== client.personType);
  }

  private clearCustomerModeBackup(): void {
    this.customerCopy = null;
    this.customerIdCopy = null;
    this.isClientWithRFCCopy.set(this.isClientWithRFC());
  }

  private restoreCustomerMode(): void {
    if (!this.customerCopy) {
      return;
    }

    this.formCustomer.patchValue(this.customerCopy);
    this.selectedClient.set(this.customerIdCopy !== null ? this.customerCopy : null);
    if (this.customerIdCopy !== null) {
      this.selectedCustomerId.emit(this.customerIdCopy);
    }
    this.isClientWithRFC.set(this.isClientWithRFCCopy());
    this.clearCustomerModeBackup();
    this.validatorChange();
  }

  private isClosedQuote(): boolean {
    return isStandardQuoteCustomerOnlyLayout(this.quoteService.formData?.status);
  }

  openEditClient() {
    if (this.forceReadOnly) {
      return;
    }
    this.modalService.component = this.modalNewClient;
    this.modalService.openModal();
  }

  cancelModal(event: any) {
    if(event === FormStep.STEP_RFC_SEARCH || event === FormStep.STEP_NEW_CLIENT){
      if (this.customerCopy) {
        this.restoreCustomerMode();
      } else {
        this.selectedClient.set(this.tempSelectedClient());
      }
      this.tempSelectedClient.set(null);
    }
  }

  save() {
    if (this.validForm(this.formCustomer)) {
      this.checked = true;
    } else {
      this.showNotificationError(
        'Revisa los campos obligatorios antes de continuar.'
      );
    }
  }

  //#region CVA
  writeValue(value: any): void {
    if (value) {
      // Ensure feriaCustomer is converted to boolean when loading data
      const patchValue = {
        ...this.normalizeCustomerPatch(value),
      } as Record<string, unknown>;
      this.previousPersonType = (patchValue['personType'] as PersonType) ?? null;
      if (this.featureLoyaltyFeriaQuotation && 'feriaCustomer' in patchValue) {
        patchValue['feriaCustomer'] = Boolean(patchValue['feriaCustomer']);
      }
      this.formCustomer.patchValue(patchValue, { emitEvent: false });
      this.applyCustomerModeFromQuote(this.customerIdSaved());
    }
  }

  /** Modo con RFC solo si la cotización está ligada a un cliente del catálogo (customerId). */
  applyCustomerModeFromQuote(customerId: number | null | undefined): void {
    if (!this.formCustomer) {
      return;
    }
    const withRfc = customerId != null && customerId !== undefined;
    this.isClientWithRFC.set(withRfc);
    this.isClientWithRFCCopy.set(withRfc);
    if (withRfc) {
      const customer = this.formCustomer.getRawValue();
      const rfcControl = this.formCustomer.controls['rfc'];
      const storedRfc =
        customer?.rfc ||
        (this.quoteService.formData?.customer as { rfc?: string } | undefined)?.rfc ||
        this.selectedClient()?.rfc;
      if (
        rfcControl &&
        storedRfc &&
        (!rfcControl.value || String(rfcControl.value).trim() === '')
      ) {
        rfcControl.setValue(storedRfc, { emitEvent: false });
      }
      this.selectedClient.set({
        ...customer,
        id: customerId,
        rfc: rfcControl?.value || storedRfc || customer?.rfc,
      });
      this.skipNameFormatValidation = false;
    } else {
      this.selectedClient.set(null);
      this.skipNameFormatValidation = true;
      // No vaciar RFC aquí: writeValue puede ejecutarse antes de que customerId esté en el padre.
    }
    this.validatorChange();
  }


  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  private onChange = (_: any) => {};
  private onTouched = () => {};

  //#endregion

  //#region Validator
  validate(_control: AbstractControl<any, any>): ValidationErrors | null {
    return this.formCustomer.valid ? null : { invalidFormCustomer: true };
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  private onValidatorChange: () => void = () => {};

  //#endregion

  onInputFocus() {
    if (this.forceReadOnly) {
      return;
    }
    this.requestOpenModal.emit();
  }

  openModalNewClient() {
    if (this.forceReadOnly) {
      return;
    }
    this.tempSelectedClient.set(this.selectedClient());
    this.selectedClient.set(null);
    this.modalService.component = this.modalNewClient;
    this.modalService.openModal();
    if (this.document) {
      this.document.body.style.overflow = 'hidden';
    }
  }

  reload() {
    this.quoteService.updateForm({ ...this.quoteService.formData, customer: this.getRawValue() });
  }

  /** Cliente feria: solo persiste el indicador en la cotización; no altera validación de nombres. */
  onFeriaCustomerToggle(value: boolean) {
    this.formCustomer.get('feriaCustomer')?.setValue(value);
  }

  openModalSelectClient() {
    if (this.forceReadOnly) {
      return;
    }
    this.modalService.component = this.modalSelectClient;
    this.modalService.openModal();
    if (this.document) {
      this.document.body.style.overflow = 'hidden';
    }
  }

  isLegalPerson() {
    return this.formCustomer.controls['personType']?.value == 'LEGAL_PERSON';
  }

  get isEditable() {
    if (this.forceReadOnly) {
      return false;
    }
    return this.selectedClient() != null &&
      this.formCustomer.controls['isFromProLeaseNet']?.value !== true &&
      this.formCustomer.controls['rfc']?.value != null;
  }

  get personType() {
    return this.formCustomer.controls['personType']?.value || '';
  }

  private get resolvedDisplayNames() {
    if (this.isLegalPerson()) {
      return null;
    }
    return this.clientsService.resolveNaturalPersonNameFields(
      this.formCustomer.getRawValue() as ClientApi
    );
  }

  get name() {
    if (this.isLegalPerson()) {
      const firstName = this.formCustomer.controls['firstName']?.value;
      const customerName = this.formCustomer.controls['customerName']?.value;
      return firstName || customerName || '';
    }
    return this.resolvedDisplayNames?.firstName ?? '';
  }

  get lastName() {
    return this.resolvedDisplayNames?.lastName ?? '';
  }

  get mothersLastName() {
    return this.resolvedDisplayNames?.mothersLastName ?? '';
  }

  get secondName() {
    return this.formCustomer.controls['secondName']?.value || '';
  }

  get rfc() {
    return this.formCustomer.controls['rfc']?.value || '';
  }

  get formHasRFC() {
    return this.rfc !== null && this.rfc !== undefined && this.rfc.trim() !== '';
  }

  onCheckboxChange(event: Event) {
    if (this.forceReadOnly) {
      return;
    }
    const target = event.target as HTMLInputElement;

    this.enableStrictNameValidation();

    if(target.checked === this.isClientWithRFCCopy()) {
      // Restoring previous data (going back to previous state)
      this.formCustomer.patchValue(this.customerCopy || {});
      if(this.customerIdCopy && this.customerIdCopy !== null) {
        this.selectedCustomerId.emit(this.customerIdCopy);
      }
      this.customerCopy = null;
      this.customerIdCopy = null;
    } else {
      // Switching modes - save current data
      this.isClientWithRFCCopy.set(this.isClientWithRFC());
      this.customerCopy = this.formCustomer.getRawValue();
      this.customerIdCopy = this.selectedClient()?.id;

      if(this.customerIdCopy && this.customerIdCopy !== null) {
        this.selectedCustomerId.emit(null as any);
        // Also clear the customerId in the main form
        if (this.quoteService.formData) {
          (this.quoteService.formData as any).customerId = null;
        }
      }
      this.selectedClient.set(null);

      // Clear form when switching modes
      this.formCustomer.patchValue({
        id: null,
        creatioDate: null,
        firstName: '',
        secondName: '',
        lastName: '',
        mothersLastName: '',
        rfc: '',
        customerName: '',
        isFromProLeaseNet: false,
        feriaCustomer: false,
      });

      this.formCustomer.get('rfc')?.updateValueAndValidity();
    }
    this.isClientWithRFC.set(target.checked);
    if (!target.checked) {
      this.selectedCustomerId.emit(null as any);
      if (this.quoteService.formData) {
        (this.quoteService.formData as any).customerId = null;
      }
    }
    this.validatorChange();
  }

  onEdit() {
    // Method for blur events
  }

  // Transform customer data for backend
  public transformCustomerData(value: any) {
    if (!value) return value;

    if (!this.isClientWithRFC()) {
      const transformedData: Record<string, unknown> = {
        personType: value.personType || '',
        firstName: value.firstName || '',
        lastName: value.lastName || '',
        mothersLastName: value.mothersLastName || null,
        secondName: value.secondName || null,
        rfc: null,
        isFromProLeaseNet: false,
        customerName: value.firstName || '',
        id: null,
        creatioDate: null,
      };
      if (this.featureLoyaltyFeriaQuotation) {
        transformedData['feriaCustomer'] = Boolean(value['feriaCustomer']);
      }

      Object.keys(transformedData).forEach((key) => {
        if ((transformedData as any)[key] === null) {
          delete (transformedData as any)[key];
        }
      });

      return transformedData;
    }
    const out = { ...value };
    if (!this.featureLoyaltyFeriaQuotation) {
      delete (out as any).feriaCustomer;
    } else {
      (out as any).feriaCustomer = Boolean((out as any).feriaCustomer);
    }
    return out;
  }

  // Override getRawValue to always return transformed data
  getRawValue() {
    const rawValue = this.formCustomer.getRawValue();
    return this.transformCustomerData(rawValue);
  }

  allowAlphabeticCharacter(event: KeyboardEvent) {
    // Para persona moral, permitir todo sin restricciones
    // No hacer nada, permitir que el input acepte cualquier carácter
    return;
  }

  /**
   * Previene la escritura de números y caracteres especiales para persona física.
   * Permite letras (incluyendo acentos y ñ) y espacios.
   * Bloquea específicamente: números y #$%(.
   */
  allowOnlyLettersAndNumbers(event: KeyboardEvent) {
    // Usar event.key que es más confiable que event.which
    const key = event.key;

    // Permitir teclas de control (Backspace, Delete, Tab, Arrow keys, etc.)
    const controlKeys = [
      'Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'Enter', 'Escape'
    ];

    if (controlKeys.includes(key)) {
      return; // Permitir teclas de control
    }

    // Permitir Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if (event.ctrlKey || event.metaKey) {
      return; // Permitir atajos de teclado
    }

    // Bloquear específicamente los caracteres prohibidos: #$%&(
    const forbiddenChars = /[#$%&(]/;
    if (forbiddenChars.test(key)) {
      event.preventDefault();
      return;
    }

    // Bloquear números (0-9)
    if (/[0-9]/.test(key)) {
      event.preventDefault();
      return;
    }

    // Solo permitir letras (a-z, A-Z, acentos, ñ) y espacios
    const allowedPattern = /^[A-Za-zÀ-ÿ\u00f1\u00d1 ]$/;
    if (!allowedPattern.test(key)) {
      event.preventDefault();
    }
  }

  /**
   * Limpia el valor del input removiendo caracteres no permitidos.
   * Se ejecuta en el evento input para limpiar cualquier carácter que haya pasado.
   * Solo mantiene letras (incluyendo acentos y ñ) y espacios, sin números ni otros caracteres.
   */
  cleanNaturalPersonInput(event: Event) {
    this.enableStrictNameValidation();
    const target = event.target as HTMLInputElement;
    const currentValue = target.value || '';

    // Remover números, caracteres prohibidos (#$%&() y cualquier otro carácter especial
    // Solo mantener letras (incluyendo acentos y ñ) y espacios
    const cleanedValue = currentValue.replace(/[^A-Za-zÀ-ÿ\u00f1\u00d1 ]/g, '');

    // Si el valor cambió, actualizarlo
    if (cleanedValue !== currentValue) {
      target.value = cleanedValue;

      // Actualizar el valor del formulario
      const controlName = target.getAttribute('formControlName');
      if (controlName) {
        this.formCustomer.get(controlName)?.setValue(cleanedValue, { emitEvent: false });
        this.formCustomer.get(controlName)?.updateValueAndValidity();
      }
    }
  }

  /**
   * Limpia el contenido pegado, permitiendo letras (incluyendo acentos y ñ) y espacios.
   * Remueve números, #$%( y otros caracteres especiales.
   */
  onPasteNaturalPerson(event: ClipboardEvent) {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    // Remover números, caracteres prohibidos (#$%&() y otros caracteres especiales; mantener letras y espacios
    const cleanedText = pastedText.replace(/[^A-Za-zÀ-ÿ\u00f1\u00d1 ]/g, '');

    const target = event.target as HTMLInputElement;
    const start = target.selectionStart || 0;
    const end = target.selectionEnd || 0;
    const currentValue = target.value || '';

    // Insertar el texto limpio en la posición del cursor
    const newValue = currentValue.substring(0, start) + cleanedText + currentValue.substring(end);
    target.value = newValue;

    // Actualizar el valor del formulario
    const controlName = target.getAttribute('formControlName');
    if (controlName) {
      this.formCustomer.get(controlName)?.setValue(newValue);
      this.formCustomer.get(controlName)?.updateValueAndValidity();
    }

    // Restaurar la posición del cursor
    setTimeout(() => {
      target.setSelectionRange(start + cleanedText.length, start + cleanedText.length);
    }, 0);
  }
}
